import React from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaCheck, FaCreditCard, FaStar } from "react-icons/fa";

const Sales = () => {
  const features = [
    {
      title: "AI-Powered Hairstyle Try-On",
      description: "Instantly see yourself in various cuts and colors with our AI-powered virtual try-on tool."
    },
    {
      title: "Endless Style Possibilities",
      description: "Choose from hundreds of trendy hairstyles for men and women."
    },
    {
      title: "Realistic Results",
      description: "Our advanced AI ensures natural-looking results that match your facial features."
    }
  ];

  const testimonials = [
    {
      name: "Sarah L.",
      review: "I found my perfect hairstyle in minutes! The AI try-on is so realistic and easy to use.",
      rating: 5
    },
    {
      name: "Mike T.",
      review: "This tool saved me from a bad haircut. Highly recommend it!",
      rating: 5
    },
    {
      name: "Emily R.",
      review: "The eBook is a game-changer. So many styles to try, and the tutorials are super helpful.",
      rating: 5
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Hairstyle Try-On with AI – Discover Your Perfect Look",
    "description": "Explore endless hairstyle possibilities with our virtual hairstyle try-on with AI. Instantly see yourself in various cuts and colors to find the perfect match for your style.",
    "applicationCategory": "Beauty & Lifestyle",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": features.map(f => f.title).join(", ")
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Hairstyle Try-On with AI – Discover Your Perfect Look</title>
        <meta name="description" content="Explore endless hairstyle possibilities with our virtual hairstyle try-on with AI. Instantly see yourself in various cuts and colors to find the perfect match for your style." />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[630px] md:h-[830px] py-20 flex items-center bg-gradient-to-br from-teal-900 via-teal-700 to-emerald-500 flex-col" style={{ overflow: "visible" }}>
        <div className="container relative z-10 px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white drop-shadow-lg mb-6">
              <span className="block">Don't Make Poor Choices.</span> 
              <span className="text-teal-300">Save Hundreds of Dollars</span>
              <span className="block">(And Bad Hair Days) With AI</span>
            </h1>
            <h5 className="text-lg md:text-xl lg:text-2xl font-semibold text-white mb-8">
              <span className="text-red-500">BONUS:</span>{" "}
              Try on hairstyle with AI and{" "}
              <span className="text-teal-300">Get your FREE download eBook!</span>{" "}
              from Braids to Buns to Bold new colors Step-By-Step Tutorials for 82 fabulous hairstyles.
            </h5>


            {/* Video Section */}
        <div className="relative w-full max-w-4xl mx-auto mt-10 z-20 px-4">
          <div className="relative overflow-hidden rounded-2xl shadow-lg" style={{ paddingTop: "56.25%" }}>
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src="https://www.youtube.com/embed/qBuxEFjzqlA"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>

          </motion.div>
        </div>

      </section>

      {/* Features Section */}
      <section className="py-12 md:py-20 bg-white "> 
      <p className="text-black text-xs md:text-xl lg:text-xs mb-2 mt-4 font-semibold text-center">Over 3,000 people are using AI Hairstyle Studio. Your hairstyle may take 60 seconds to generate.</p>
      {/* <p className="text-black text-s md:text-xl lg:text-s font-semibold mb-4 mt-2 text-center">Click The <span className="text-teal-300">"Try On New Hairstyle"</span> Button Below to Get Started + Receive Your FREE EBook, Sent To You By Email!</p> */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/try-on"
                className="btn bg-teal-500 mx-auto mt-2 hover:bg-teal-600 text-center text-2xl md:text-1xl text-white px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Try On a New Hairstyle <br />
                {/* <span className="ml-2 text-white text-sm md:text-lg">(Click here to get access)</span> */}
              </Link>
            </div>
            {/* Payment Icons */}
            <div className="mt-6 flex flex-col items-center justify-center space-y-2">
              <span className="text-gray-500 font-semibold text-sm md:text-lg">🔒 Guaranteed <span className="font-bold">safe & secure</span> checkout</span>
              <span className="bg-black text-gray-200 text-xs rounded-lg p-1">Powered by <span className="font-bold text-sm md:text-lg">stripe</span></span>
            </div>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {["visa", "mastercard", "amex", "jcb", "discover", "diners", "unionpay"].map((icon, index) => (
                <img key={index} src={`/sales/${icon}-icon.png`} alt={icon} className="h-6 md:h-8" />
              ))}
            </div>
      </section>

      {/* Ebook Section */}
      <section className="py-12 md:py-2 bg-white">
        <div className="container px-4">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 text-center mb-6 md:mb-8">
            Here's What You're Getting When Purchasing Your First AI Hairstyle Generator
          </h2>
          <h3 className="text-lg md:text-xl font-bold text-teal-500 text-center mb-6">
            📖 [PDF Ebook] Braids, Buns, and Twists!: Step-By-Step Tutorials for 82 Fabulous Hairstyles, Which Can't Be Found On Social Media, Youtube, TiKTok or Facebook. 
          </h3>
          <div className="w-full max-w-3xl mx-auto mb-8">
            <img
              src="/sales/ebook.png"
              alt="Ebook Preview"
              className="w-full rounded-lg shadow-lg"
            />
          </div>
          <p className="text-teal-500 text-base md:text-lg font-semibold text-center mb-8">
            From top knots to French twists, runway-chic hair is now at your fingertips! Whether you're styling for a party, work, beach day, or a glam night out, this step-by-step guide has 80+ classic & trendy hairstyles with easy-to-follow tutorials.
          </p>
          <h3 className="text-2xl md:text-4xl font-bold text-teal-500 text-center mb-6 md:mb-8">
            What’s Inside?
          </h3>
          <ul className="max-w-2xl mx-auto space-y-4 text-base md:text-lg font-medium text-gray-700 mb-12">
            {[
              "Step-by-step styling guides.",
              "Stunning fashion photography.",
              "Hair tips tailored for all hair types and lengths.",
              "Product recommendations to achieve a flawless finish every time.",
            ].map((item, index) => (
              <li key={index} className="flex items-start space-x-4">
                <span className="flex-shrink-0 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center text-white">✔</span>
                <span className="text-gray-800">{item}</span>
              </li>
            ))}
          </ul>

          {/* <p className="text-gray-500 text-base md:text-lg font-semibold text-center mb-8">
            Click The <span className="text-teal-500">"Try On New Hairstyle"</span> Button Below to Get Started + Receive Your <span className="text-teal-500">FREE EBook</span>, Sent To You By Email!
          </p> */}
          
        </div>
      </section>


      {/* Testimonials Section */}
      <section className="py-12 md:py-10 bg-gray-50">

      <div className="container px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900  m8 md:mb-12">
            Why Choose AI Hairstyle Studio?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-white p-6 md:p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="container px-4">
          <h2 className="text-2xl mt-12 md:text-3xl font-bold text-center text-gray-900 mb-8 md:mb-12">
            What Our Customers Are Saying
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-white p-6 md:p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-center space-x-2 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-500" />
                  ))}
                </div>
                <p className="text-sm md:text-base text-gray-600 mb-4">{testimonial.review}</p>
                <p className="text-gray-900 font-semibold">– {testimonial.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-500 relative overflow-hidden">
        <div className="container relative z-10 text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-4xl font-bold mb-6 text-white drop-shadow-lg">
              Ready to Transform Your Look?
            </h2>
            <p className="text-base md:text-xl mb-8 text-white/90 font-light max-w-3xl mx-auto leading-relaxed">
              Join thousands of satisfied customers who have found their perfect hairstyle.
            </p>
            <Link
              to="/try-on"
              className="inline-block bg-white hover:bg-gray-50 text-teal-600 font-semibold px-6 md:px-8 py-3 md:py-4 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              Start Your Journey
            </Link>
            {/* Payment Icons */}
            <div className="mt-6 flex flex-col items-center justify-center space-y-2">
              <span className="text-gray-200 font-semibold text-sm md:text-lg">🔒 Guaranteed <span className="font-bold">safe & secure</span> checkout</span>
              <span className="bg-black text-gray-200 text-xs rounded-lg p-1">Powered by <span className="font-bold text-sm md:text-lg">stripe</span></span>
            </div>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {["visa", "mastercard", "amex", "jcb", "discover", "diners", "unionpay"].map((icon, index) => (
                <img key={index} src={`/sales/${icon}-icon.png`} alt={icon} className="h-6 md:h-8" />
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Sales;
